
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Props {
    children?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 py-8">
                    <div className="w-full max-w-md text-center space-y-6">

                        {/* Icon */}
                        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                            <AlertTriangle className="w-8 h-8 text-red-600" />
                        </div>

                        {/* Text */}
                        <div className="space-y-2">
                            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Something went wrong</h1>
                            <p className="text-gray-500 text-lg">
                                We're sorry, but an unexpected error occurred. Our team has been notified.
                            </p>
                        </div>

                        {/* Error Details (Only in Dev) */}
                        {import.meta.env.DEV && this.state.error && (
                            <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-left overflow-auto max-h-48 text-sm font-mono text-red-800">
                                {this.state.error.toString()}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                            <button
                                onClick={() => window.location.reload()}
                                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full sm:w-auto transition-colors"
                            >
                                <RefreshCcw className="w-4 h-4 mr-2" />
                                Reload Page
                            </button>

                            <Link
                                to="/"
                                onClick={() => this.setState({ hasError: false })}
                                className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full sm:w-auto transition-colors"
                                style={{ textDecoration: 'none' }} // Ensure Link styles don't conflict
                            >
                                <Home className="w-4 h-4 mr-2" />
                                Go Home
                            </Link>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
